import { Navigate, useParams } from 'react-router-dom';
import { firstEnabledFeature, type UiFeature } from '@repo/shared-types';
import { useAuth } from '../../lib/auth';
import type { ReactNode } from 'react';

function Loading() {
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-orange-200 dark:border-orange-900 border-t-orange-500 rounded-full animate-spin" />
        <p className="text-sm text-ink-faint">Loading…</p>
      </div>
    </div>
  );
}

function NoSectionsEnabled() {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6">
      <div className="max-w-sm text-center">
        <p className="text-sm font-medium text-ink-secondary mb-1.5">No sections enabled</p>
        <p className="text-xs text-ink-faint mb-4">
          Your account has every section of this console switched off. An administrator needs to
          restore access before you can use it.
        </p>
        <button onClick={logout} className="text-[11px] text-orange-500 hover:text-orange-700 transition-colors">
          Sign out
        </button>
      </div>
    </div>
  );
}

/**
 * Requires a session, and optionally that a UI feature is enabled for the current user.
 *
 * The feature check lives on the route rather than only in the sidebar because hiding a link
 * is not a gate — a bookmark or a pasted URL would otherwise still render the page. A user who
 * lands on a disabled section is redirected to their first enabled one instead of being shown
 * an error, since from their point of view the section simply doesn't exist.
 *
 * This is visibility, not authorization: the REST API still serves these resources to any
 * project member. Membership and role remain the real access control.
 */
export function ProtectedRoute({ children, feature }: { children: ReactNode; feature?: UiFeature }) {
  const { isAuthenticated, isLoading, hasFeature, features } = useAuth();
  const { projectSlug } = useParams<{ projectSlug: string }>();

  if (isLoading) return <Loading />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!feature || hasFeature(feature)) return <>{children}</>;

  const fallback = firstEnabledFeature(features);
  if (!fallback) return <NoSectionsEnabled />;
  // Most sections are org-level and live at a bare path; sending someone to `/p/<slug>/feed` after
  // Feed moved out from under the project prefix is how a redirect lands on a route that no longer
  // exists and bounces straight back here.
  if (fallback.orgLevel) return <Navigate to={`/${fallback.path}`} replace />;
  // A project-scoped fallback with no project in the URL has nowhere section-shaped to go; the
  // organization at the root of the tree is always reachable.
  if (!projectSlug) return <Navigate to="/" replace />;
  return <Navigate to={`/p/${projectSlug}/${fallback.path}`} replace />;
}
